/**
 * Internal-prefix constants used by the binding enhancer walker.
 *
 * These are the grammar anchors from ADR-11 (child SAD §4). Kept as
 * standalone string exports so test harnesses can reference them
 * without importing the heavy HOC.
 */
export const BIND_PREFIX = '_bind.';
export const ACTION_PREFIX = '_action.';
export const META_PREFIX = '_meta.';
export const FORM_KEY_PROP = 'formKey';

/**
 * Authored opt-in: commit the first resolved option when the bound
 * `formKey` holds nothing yet.
 *
 * Distinct from {@link ComponentBindingMeta.autoSelectSingleOption}, which is a
 * registration-level property of a component ("a one-item dropdown is not a
 * choice, so commit it and lock the control"). This is a per-node authoring
 * decision about a list of many: a picker whose dependent charts would
 * otherwise render empty until someone touches it wants a first selection, and
 * the picker beside it may not.
 *
 * Stripped like `formKey` — the component never sees it.
 */
export const AUTO_SELECT_FIRST_OPTION_PROP = 'autoSelectFirstOption';

/**
 * Authored opt-in for `MultiSelect`: while its options source is
 * refreshing, keep passing the list already on screen. A
 * refresh clears source data before the next payload arrives, and the
 * options transform turns that gap into `[]`. The open menu would
 * collapse and replay its entrance. A settled list, including a settled
 * empty one, still replaces what is showing.
 *
 * Stripped like `formKey` — the component never sees it. Off unless the
 * authored value is the literal `true`.
 */
export const KEEP_OPTIONS_WHILE_LOADING_PROP = 'keepOptionsWhileLoading';

/**
 * Default handler-prop search order used when wiring `formKey`.
 * Components override via {@link ComponentBindingMeta.eventPropNames}.
 */
export const DEFAULT_EVENT_PROP_NAMES: readonly string[] = [
  'onChange',
  'onSelect',
];

/**
 * Companion prop names for `transform: 'options'`. Collected from
 * `_bind.<companion>` during the walker pass and consumed by
 * {@link applyOptions}.
 */
export const OPTION_LABEL_FIELD = 'optionLabelField';
export const OPTION_VALUE_FIELD = 'optionValueField';

/**
 * Companion prop name for `transform: 'tagLabels'`. Authors set
 * `_bind.tagKeys` to an array of entry keys to whitelist and order
 * which `{ label, value }` entries become tag labels. Collected from
 * the prop bag during the walker pass and consumed by
 * {@link applyTagLabels}; never reaches the HEN UI component.
 */
export const TAG_KEYS_FIELD = 'tagKeys';

/**
 * Companion prop name for `transform: 'tagLabels'`. Authors set
 * `_bind.defaultTagKeys` to the key list used when the primary
 * `_bind.tagKeys` resolves to an empty list (e.g. a `formState`
 * binding before the user selects anything). This lets a single
 * `tagLabels` binding show a bounded default set pre-interaction and
 * narrow to only the selected keys once `tagKeys` is populated, without
 * falling all the way back to "every affirmative entry". Collected from
 * the prop bag during the walker pass and consumed by
 * {@link applyTagLabels}; never reaches the HEN UI component.
 */
export const DEFAULT_TAG_KEYS_FIELD = 'defaultTagKeys';

/**
 * Companion prop names for `transform: 'stats'`. Authors set
 * `_bind.statLabelField` / `_bind.statValueField` / `_bind.statTooltipField`
 * to the entry field names the transform reads when projecting an
 * array-of-objects into `{ label, value, tooltip? }` stat fields.
 * Collected from the prop bag during the walker pass and consumed by
 * `applyStats`; never reach the HEN UI component.
 */
export const STAT_LABEL_FIELD = 'statLabelField';
export const STAT_VALUE_FIELD = 'statValueField';
export const STAT_TOOLTIP_FIELD = 'statTooltipField';

/**
 * Companion prop name for `transform: 'badges'`. When set and an entry
 * is an indexable object, authors project `String(entry[badgeField])`
 * into the emitted `string[]`; otherwise `String(entry)` is used.
 * Collected from the prop bag during the walker pass and consumed by
 * `applyBadges`; never reaches the HEN UI component.
 */
export const BADGE_FIELD = 'badgeField';

/**
 * Reserved `_action.<event>` names that are SYNTHETIC — fired by
 * the binding-enhancer itself rather than by the underlying HEN UI
 * component. They are recognised by `resolveActions` exactly like
 * any other action declaration, but the bound-component composer
 * MUST NOT wire them onto a React event prop (they aren't real
 * DOM/React events; passing them through would land an unknown
 * prop on the catalog component).
 *
 * `onSourceChange`: fired by `useOnSourceChange` in
 * `createBoundRender` when any data source this component is bound
 * to settles to a NEW value after the first settled snapshot.
 * Backs the cascading-select reset pattern via the
 * `formState.clear` action — see the SAD's Action Type Registry.
 */
export const SYNTHETIC_ACTION_EVENTS: readonly string[] = ['onSourceChange'];

/**
 * `true` when `eventName` is a synthetic event the binding-enhancer
 * fires itself, NOT a React/DOM event. Centralised so any future
 * synthetic event (e.g. `onResolve`, `onError`) can be added in
 * one place without hunting through composer code paths.
 */
export const isSyntheticActionEvent = (eventName: string): boolean => {
  for (let i = 0; i < SYNTHETIC_ACTION_EVENTS.length; i++) {
    if (SYNTHETIC_ACTION_EVENTS[i] === eventName) return true;
  }
  return false;
};

/** The synthetic event used by the cascading-select reset pattern. */
export const ON_SOURCE_CHANGE_EVENT = 'onSourceChange';

/**
 * Prop slot whose bound value feeds a component's registered render adapter.
 *
 * Fixed rather than per-registration. An adapter's input is one row set, and
 * letting each component rename the slot would mean reading a registration to
 * know what `_bind.rows` means in a document.
 */
export const ADAPTER_ROWS_PROP = 'rows';

/** The `_bind.` key an author writes to feed a render adapter. */
export const ADAPTER_ROWS_BIND_KEY = `${BIND_PREFIX}${ADAPTER_ROWS_PROP}`;

/**
 * Prefixes reserved by the binding enhancer grammar (child SAD §11
 * Security Model, ADR-11). Any prop key starting with one of these is
 * binding-language input — declarative data, not a Puck slot, not a
 * React prop, never user-callable.
 *
 * Centralized so structural visitors over Puck `Data` (e.g. the slot
 * inferrer in `DslConfig.tsx`) can ask one source of truth whether a
 * key is reserved. See {@link isBindingLanguageKey}.
 */
export const RESERVED_BINDING_KEY_PREFIXES: readonly string[] = [
  BIND_PREFIX,
  ACTION_PREFIX,
  META_PREFIX,
];

/**
 * `true` when `propKey` belongs to the binding enhancer grammar
 * (`_bind.*`, `_action.*`, `_meta.*`, or one of the reserved literals
 * `formKey` / `autoSelectFirstOption` / `keepOptionsWhileLoading`).
 *
 * Why this exists: binding-language values can carry their own
 * `type` field (e.g. `_action.onClick: { type: 'flow.forward' }`,
 * structured `_bind.options: { source, path, transform }`), which is
 * indistinguishable by shape from a Puck component node. Any
 * structural walker that classifies props by value shape (e.g.
 * "object with string `type` ⇒ slot") MUST first skip keys for which
 * this predicate returns `true` — otherwise binding props get
 * misregistered as Puck slots and the runtime replaces their value
 * with a `Slot()` DropZone function before the binding enhancer runs.
 *
 * Adding a fourth prefix? Update {@link RESERVED_BINDING_KEY_PREFIXES}
 * and every visitor inherits the new exclusion automatically.
 */
export const isBindingLanguageKey = (propKey: string): boolean => {
  if (propKey === FORM_KEY_PROP) return true;
  if (propKey === AUTO_SELECT_FIRST_OPTION_PROP) return true;
  if (propKey === KEEP_OPTIONS_WHILE_LOADING_PROP) return true;
  for (const prefix of RESERVED_BINDING_KEY_PREFIXES) {
    if (propKey.startsWith(prefix)) return true;
  }
  return false;
};
